import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Button, Copy, Frame, Hash, Info, Qr, Rows, Select } from 'regen-ui'
import RefreshCw from '~icons/lucide/refresh-cw'

import { checksumAddressForCopy } from '#/lib/address.js'
import * as Bridge from '#/lib/bridge.js'

const defaultChain = Bridge.sourceChains[0]

/** Deposit crypto screen — QR code + deposit address for cross-chain deposits via Relay. */
export function DepositCrypto(props: DepositCrypto.Props) {
  const {
    address,
    amount,
    confirming,
    depositAddress,
    onBack,
    onDismiss,
    onDone,
    subtitle,
    title,
  } = props

  return (
    <Frame
      subtitle={
        subtitle ??
        (amount ? (
          <>
            Deposit <span className="text-foreground">{amount}</span> to continue.
          </>
        ) : (
          'Deposit funds to continue.'
        ))
      }
      onDismiss={onDismiss}
      title={title ?? 'Deposit crypto'}
      footer={
        <>
          <div className="flex gap-3">
            <Button className="flex-1" onClick={onBack} size="medium" variant="secondary">
              Back
            </Button>
            <Button
              className="flex-1"
              loading={confirming}
              onClick={onDone}
              size="medium"
              variant="primary"
            >
              {confirming ? 'Confirming…' : 'Done'}
            </Button>
          </div>
        </>
      }
    >
      <DepositCryptoContent
        address={address}
        amount={amount}
        depositAddress={depositAddress}
        token={props.token}
      />
    </Frame>
  )
}

/** Relay-backed deposit address, network, token, and safety warning controls. */
export function DepositCryptoContent(props: DepositCryptoContent.Props) {
  const { address, amount, depositAddress: depositAddress_static, networks, token } = props
  const networkOptions = useMemo(
    () =>
      (networks ?? Bridge.sourceChains).filter(
        (chain) => chain.id !== Bridge.destinationChain.id,
      ),
    [networks],
  )
  const initialToken = useMemo(() => Bridge.resolveToken(token), [token])
  const initialChain = useMemo(() => {
    if (initialToken && networkOptions.some((chain) => chain.id === initialToken.chain.id))
      return initialToken.chain
    return networkOptions[0] ?? defaultChain
  }, [initialToken, networkOptions])

  // User-selected origin chain and token.
  const [selectedChainId, setSelectedChainId] = useState<number>(initialChain.id)
  const [selectedTokenAddress, setSelectedTokenAddress] = useState<string>(
    initialToken?.chain.id === initialChain.id
      ? initialToken.token.address
      : initialChain.tokens[0].address,
  )

  useEffect(() => {
    if (!initialToken) return
    if (!networkOptions.some((chain) => chain.id === initialToken.chain.id)) return
    setSelectedChainId(initialToken.chain.id)
    setSelectedTokenAddress(initialToken.token.address)
  }, [initialToken, networkOptions])

  // Resolve the selected chain/token from the static config.
  const tokens = useMemo(() => Bridge.getTokens(selectedChainId), [selectedChainId])
  const selectedToken = useMemo(
    () => tokens.find((t) => t.address === selectedTokenAddress) ?? tokens[0],
    [tokens, selectedTokenAddress],
  )
  const selectedChain = useMemo(
    () => networkOptions.find((chain) => chain.id === selectedChainId) ?? defaultChain,
    [networkOptions, selectedChainId],
  )

  // Fetch a Relay deposit address for cross-chain routes.
  // Disabled when a static depositAddress is provided (design/preview mode).
  const deposit = Bridge.useDepositAddress({
    account: address,
    origin: {
      chainId: selectedChainId,
      token: selectedToken?.address ?? '',
      decimals: selectedToken?.decimals ?? 6,
    },
    enabled: !!selectedToken && !depositAddress_static,
  })

  const amountUsd = useMemo(() => {
    if (!amount) return 0
    const parsed = Number.parseFloat(amount.replace(/[^\d.]/g, ''))
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
  }, [amount])

  const price = Bridge.useDepositPrice({
    origin: {
      chainId: selectedChainId,
      token: selectedToken?.address ?? '',
      decimals: selectedToken?.decimals ?? 6,
    },
    destinationAmountUsd: amountUsd,
    enabled: !!selectedToken && amountUsd > 0,
  })

  const isUnsupported =
    deposit.isError && (deposit.error as Error & { code?: string }).code === 'UNSUPPORTED_ROUTE'

  const depositAddress = depositAddress_static ?? deposit.data?.address
  const namespace = Bridge.getNamespace(selectedChainId)

  // EIP-681: ethereum:<address>@<chainId>
  const qrValue = useMemo(() => {
    if (!depositAddress) return undefined
    if (namespace === 'solana') return `solana:${depositAddress}`
    return `ethereum:${depositAddress}@${selectedChainId}`
  }, [depositAddress, namespace, selectedChainId])

  return (
    <>
      <div className="flex h-[246px] flex-col gap-3 overflow-hidden rounded-body bg-pane px-4 py-3.5">
        <p className="text-label-13 text-foreground-secondary">Deposit address</p>

        {deposit.isFetching ? (
          <div className="flex animate-pulse justify-center py-2 opacity-10">
            <Qr value="0x0000000000000000000000000000000000000000" />
          </div>
        ) : isUnsupported ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center text-label-13 text-foreground-secondary">
            <p>This route is not supported.</p>
            <p className="text-label-12">Try a different network or token.</p>
          </div>
        ) : deposit.isError ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <p className="text-center text-label-13 text-foreground-secondary">
              Failed to get deposit address.
            </p>
            <Button onClick={() => deposit.refetch()} size="small" variant="secondary">
              <RefreshCw className="size-3.5" />
              Retry
            </Button>
          </div>
        ) : (
          <>
            <div className="flex justify-center py-2">{qrValue && <Qr value={qrValue} />}</div>
            {depositAddress && (
              <div className="flex items-center justify-center gap-1.5">
                <Hash
                  className="text-label-13 text-foreground-secondary"
                  isEvm={namespace === 'eip155'}
                  value={depositAddress}
                />
                <Copy
                  size="xs"
                  title="Copy address"
                  value={
                    namespace === 'eip155' ? checksumAddressForCopy(depositAddress) : depositAddress
                  }
                />
              </div>
            )}
          </>
        )}
      </div>

      <Rows className="rounded-body border border-border" dividers="full" variant="surface">
        <Rows.Row className="text-label-13" height={44} label="Network">
          <Select
            aria-label="Network"
            className="h-8 border border-border bg-surface hover:bg-surface-hover active:bg-surface-active focus-visible:bg-surface"
            items={networkOptions.map((chain) => ({
              label: chain.name,
              value: String(chain.id),
            }))}
            onChange={(value) => {
              const chainId = Number(value)
              setSelectedChainId(chainId)
              const defaultToken = Bridge.getDefaultToken(chainId)
              if (defaultToken) setSelectedTokenAddress(defaultToken.address)
            }}
            value={String(selectedChainId)}
          />
        </Rows.Row>
        <Rows.Row className="text-label-13" height={44} label="Token">
          <Select
            aria-label="Token"
            className="h-8 border border-border bg-surface hover:bg-surface-hover active:bg-surface-active focus-visible:bg-surface"
            items={tokens.map((token) => ({
              label: token.symbol,
              value: token.address,
            }))}
            onChange={setSelectedTokenAddress}
            value={selectedTokenAddress}
          />
        </Rows.Row>
      </Rows>

      <Info
        message={
          <>
            Only send{' '}
            <span className="text-warning-strong">
              {formatOriginAmount({
                amountUsd,
                originAmount: price.data?.originAmount,
                symbol: selectedToken?.symbol,
              })}
            </span>{' '}
            on <span className="text-warning-strong">{selectedChain.name}</span>. Sending other
            tokens or using a different network may result in permanent loss.
          </>
        }
        type="warning"
      />

      <Link
        className="self-center text-label-13 text-foreground-secondary underline decoration-border underline-offset-2 transition-colors hover:text-foreground hover:decoration-foreground focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
        rel="noreferrer"
        search={{
          chainId: selectedChainId,
          token: selectedToken?.address ?? selectedTokenAddress,
        }}
        target="_blank"
        to="/deposit-faq"
      >
        Where are my funds
      </Link>
    </>
  )
}

/** `DepositCryptoContent` types. */
export declare namespace DepositCryptoContent {
  /** Props for {@link DepositCryptoContent}. */
  type Props = {
    /** User's Tempo address — shown directly when Tempo is selected. */
    address: string
    /** Formatted fiat amount needed, e.g. "$50.00". */
    amount?: string | undefined
    /** Static deposit address — skips the Relay fetch when provided. */
    depositAddress?: string | undefined
    /** Networks available in the network selector. */
    networks?: readonly Bridge.Chain[] | undefined
    /** Token address or supported deposit token symbol to pre-fill. */
    token?: string | undefined
  }
}

function formatOriginAmount(input: {
  amountUsd: number
  originAmount: string | undefined
  symbol: string | undefined
}) {
  const { amountUsd, originAmount, symbol } = input
  const tokenLabel = symbol ?? 'the correct token'
  if (!originAmount || amountUsd <= 0) return tokenLabel
  const parsed = Number.parseFloat(originAmount)
  if (!Number.isFinite(parsed) || parsed <= 0) return tokenLabel
  const digits = parsed >= 1 ? 2 : parsed >= 0.01 ? 4 : parsed >= 0.0001 ? 6 : 8
  const formatted = parsed.toLocaleString('en-US', {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  })
  return `${formatted} ${tokenLabel}`
}

/** @internal */
export declare namespace DepositCrypto {
  /** Props for the {@link DepositCrypto} component. */
  type Props = {
    /** User's Tempo address — shown directly when Tempo is selected. */
    address: string
    /** Formatted fiat amount needed, e.g. "$50.00". */
    amount?: string | undefined
    /** Whether the deposit is being confirmed (waiting for funds to arrive). */
    confirming?: boolean | undefined
    /** Static deposit address — skips the Relay fetch when provided (design/preview mode). */
    depositAddress?: string | undefined
    /** Called when user clicks "Back". */
    onBack: () => void
    /** Called when user dismisses the frame. */
    onDismiss?: (() => void) | null | undefined
    /** Called when user clicks "Done". */
    onDone: () => void
    /** Secondary text below the title. */
    subtitle?: ReactNode | undefined
    /** Primary heading text. */
    title?: ReactNode | undefined
    /** Token address or supported deposit token symbol to pre-fill. */
    token?: string | undefined
  }
}
